import React, { useState, useEffect } from 'react';
import { useNetwork } from '@/providers/NetworkProvider';
import { ethers } from 'ethers';
import { useTranslation } from 'react-i18next';
import { notify } from '@/utils/notify';
import '@/styles/hacker-aesthetic.css';
import '@/styles/achievements.css';

interface Achievement {
  id: number;
  name: string;
  description: string;
  rewardAmount: string;
  requirement: number;
  isUnlocked: boolean;
  unlockTime: number;
  progress: number;
  category: string;
}

interface AchievementsPanelProps {
  onClose: () => void;
}

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true">
    <path
      d="M13.5 4.5l-7 7L3 8"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <rect x="3" y="7" width="10" height="7" rx="1.5" />
    <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
  </svg>
);

export const AchievementsPanel: React.FC<AchievementsPanelProps> = ({ onClose }) => {
  const { contracts, signer } = useNetwork();
  const { t } = useTranslation();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [owedRewards, setOwedRewards] = useState('0');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'nodes' | 'upgrades' | 'chests' | 'earning' | 'special'>('all');

  useEffect(() => {
    if (signer && contracts) {
      loadAchievements();
    }
  }, [signer, contracts]);

  const loadAchievements = async () => {
    if (!signer || !contracts) return;

    try {
      setLoading(true);
      setLoadError(null);

      const achievementsContract = contracts.achievements;
      let allAchievements: any[] = [];
      let unlocked: boolean[] = [];
      let progress: any[] = [];

      try {
        if (achievementsContract?.owedAchievementRewards) {
          const playerAddress = await signer.getAddress();
          const owed = await achievementsContract.owedAchievementRewards(playerAddress);
          setOwedRewards(ethers.formatEther(owed));
        } else {
          setOwedRewards('0');
        }
      } catch {
        setOwedRewards('0');
      }

      if (achievementsContract) {
        try {
          const allAchievementsResult = await achievementsContract.getAllAchievements();

          if (allAchievementsResult && allAchievementsResult.length > 0) {
            allAchievements = allAchievementsResult.map((achievement: any) => ({
              id: achievement.id,
              name: achievement.name,
              description: achievement.description,
              rewardAmount: achievement.rewardAmount,
              requirement: achievement.requirement,
              category: achievement.category,
              isActive: achievement.isActive,
              unlockTime: achievement.unlockTime,
            }));

            const playerAddress = await signer.getAddress();
            const achievementData = await achievementsContract.getPlayerAchievements(playerAddress);

            if (achievementData && achievementData.length >= 2) {
              unlocked = achievementData[0] || [];
              progress = achievementData[1] || [];
            }
          }
        } catch (error) {
          console.error('Failed to load on-chain achievements:', error);
          throw error;
        }
      }

      if (allAchievements.length === 0) {
        setAchievements([]);
        setLoadError('No active achievements were returned by the contract.');
        return;
      }

      const formattedAchievements: Achievement[] = allAchievements.map((achievement: any, index: number) => {
        const isUnlocked = index < unlocked.length ? unlocked[index] : false;
        const achievementProgress = index < progress.length ? progress[index] : 0;

        return {
          id: achievement.id,
          name: achievement.name,
          description: achievement.description,
          rewardAmount: achievement.rewardAmount ? ethers.formatEther(achievement.rewardAmount) + ' ETCPOWv2' : '0 ETCPOWv2',
          requirement: Number(achievement.requirement) || 0,
          isUnlocked: isUnlocked,
          unlockTime: Number(achievement.unlockTime) || 0,
          progress: achievementProgress,
          category: achievement.category || 'special'
        };
      });

      setAchievements(formattedAchievements);
    } catch (error) {
      console.error('Failed to load achievements:', error);
      setAchievements([]);
      setLoadError('Could not load achievements from the network.');
      notify.error(error, 'Could not load on-chain achievements');
    } finally {
      setLoading(false);
    }
  };

  const claimOwedRewards = async () => {
    if (!contracts.achievements) return;
    try {
      setClaiming(true);
      const tx = await contracts.achievements.claimOwedAchievementRewards();
      await tx.wait();
      notify.success('Achievement rewards claimed');
      await loadAchievements();
    } catch (error) {
      console.error('Failed to claim achievement rewards:', error);
      notify.error(error, 'Could not claim owed rewards — pool may be low');
    } finally {
      setClaiming(false);
    }
  };

  const getAchievementsByCategory = () => {
    switch (selectedCategory) {
      case 'nodes':
        return achievements.filter(a => a.category === 'nodes');
      case 'upgrades':
        return achievements.filter(a => a.category === 'upgrades');
      case 'chests':
        return achievements.filter(a => a.category === 'chests');
      case 'earning':
        return achievements.filter(a => a.category === 'rewards');
      case 'special':
        return achievements.filter(a => a.category === 'special');
      default:
        return achievements;
    }
  };

  const getProgressPercentage = (achievement: Achievement) => {
    if (achievement.isUnlocked) return 100;
    const requirement = Number(achievement.requirement);
    if (!requirement || requirement <= 0) return 0;
    const progress = Number(achievement.progress);
    if (!Number.isFinite(progress) || progress <= 0) return 0;
    return Math.max(0, Math.min(100, Math.floor((progress / requirement) * 100)));
  };

  const unlockedCount = achievements.filter(a => a.isUnlocked).length;
  const categoryCounts = {
    all: achievements.length,
    nodes: achievements.filter((a) => a.category === 'nodes').length,
    upgrades: achievements.filter((a) => a.category === 'upgrades').length,
    chests: achievements.filter((a) => a.category === 'chests').length,
    earning: achievements.filter((a) => a.category === 'rewards').length,
    special: achievements.filter((a) => a.category === 'special').length,
  };

  const tabs: Array<{ id: typeof selectedCategory; label: string; count: number }> = [
    { id: 'all', label: t('achievements.all'), count: categoryCounts.all },
    { id: 'nodes', label: t('achievements.nodes'), count: categoryCounts.nodes },
    { id: 'upgrades', label: t('achievements.upgrades'), count: categoryCounts.upgrades },
    { id: 'chests', label: t('achievements.chests'), count: categoryCounts.chests },
    { id: 'earning', label: t('achievements.earnings'), count: categoryCounts.earning },
    { id: 'special', label: t('achievements.special'), count: categoryCounts.special },
  ];

  return (
    <div className="ach-panel">
      <div className="ach-header">
        <div className="ach-heading">
          <div className="ach-title">{t('achievements.title')}</div>
          <span className="ach-count-chip">
            {t('achievements.unlocked')} {unlockedCount}/{achievements.length}
          </span>
        </div>
        <button className="ach-close" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {parseFloat(owedRewards) > 0 && (
        <div className="ach-owed-strip">
          <span>
            Owed rewards: <strong>{owedRewards} ETCPOWv2</strong>
          </span>
          <button
            type="button"
            className="hacker-button small success"
            disabled={claiming}
            onClick={() => void claimOwedRewards()}
          >
            {claiming ? 'CLAIMING…' : 'CLAIM OWED'}
          </button>
        </div>
      )}

      {/* Category Filter */}
      <div className="ach-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`ach-tab ${selectedCategory === tab.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(tab.id)}
          >
            <span>{tab.label}</span>
            <span className="ach-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Achievements Grid */}
      <div className="ach-body">
        {loadError && !loading ? (
          <div className="ach-load-error" role="alert">
            <div className="ach-load-error-title">ACHIEVEMENTS UNAVAILABLE</div>
            <p>{loadError}</p>
            <button type="button" className="hacker-button small" onClick={() => void loadAchievements()}>
              Retry
            </button>
          </div>
        ) : (
          <div className="ach-grid">
            {getAchievementsByCategory().map(achievement => (
            <div
              key={achievement.id}
              className={`ach-card ${achievement.isUnlocked ? 'is-unlocked' : 'is-locked'}`}
            >
              <div className="ach-card-top">
                <div className="ach-badge">
                  {achievement.isUnlocked ? <CheckIcon /> : <LockIcon />}
                </div>
                <div className="ach-reward-chip">
                  <span className="ach-reward-label">{t('achievements.reward')}</span>
                  <span className="ach-reward-amount">{achievement.rewardAmount}</span>
                </div>
              </div>

              <div className="ach-name">{achievement.name}</div>
              <div className="ach-desc">{achievement.description}</div>

              <div className="ach-progress">
                <div className="ach-progress-track">
                  <div
                    className="ach-progress-fill"
                    style={{ width: `${getProgressPercentage(achievement)}%` }}
                  ></div>
                </div>
                <div className="ach-progress-label">
                  {achievement.isUnlocked ? t('achievements.completed') : `${getProgressPercentage(achievement)}${t('achievements.progress')}`}
                </div>
              </div>
            </div>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="hacker-loading"></div>
          <span>{t('achievements.loading')}</span>
        </div>
      )}
    </div>
  );
};
